import type { Dispatch, MutableRefObject, RefObject, SetStateAction } from "react";
import chatAgentService from "../services/RealChatAgentService";
import { PARTIAL_YMMV_CARD_ENABLED } from "../config/partialYmmvCard";
import { updateSessionHash } from "./useSessionSync";
import { dispatchCardChunk } from "../components/chat/chatStreamCards";
import { TurnCapabilities } from "../components/chat/engagementAnalytics";
import { dispatchToolChunk } from "../components/chat/chatStreamTools";
import { isMessageTooLong } from "../components/chat/messageLimit";
import { classifyStreamError } from "../components/chat/chatStreamErrors";
import { ANCHOR_TOP_GAP_PX } from "./useScrollBehavior";
import { logger } from "../utils/logger";
import { postAgentResponse } from "../components/chat/hostAnalytics";
import {
  CARD_REMOVED_NOTE,
  CARD_TOKEN,
  hasCardToken,
  redactCompletingPiece,
  safeRedact,
} from "../utils/redactCritical";
import type { Message, MessagePart, SelectorCompletion } from "../types/chat.types";
import type { YMMVMatch } from "../components/chat/VehicleTypeahead";
import type { ActiveVehicle } from "../components/chat/VehicleHeaderBar";
import type { RetryScheduler } from "../services/messageRetryScheduler";
import { initiatorFromHandoffEvent, type HandoffInitiator } from "../services/handoffInitiator";

/**
 * TRAC-497 (useChatStream, final slice): the send-message + SSE streaming closure
 * extracted from ChatInterface. This hook is deliberately a pure FACTORY — it owns
 * NO state and NO effects (the retry-scheduler effects stay in the shell so mount
 * ordering is untouched); everything it reads or writes comes in via params, so the
 * returned handleSendMessage closes over exactly the same values the inline version
 * did. Behavior-preserving by construction.
 */

export interface UseChatStreamParams {
  // conversation state (shell-owned)
  messages: Message[];
  setMessages: Dispatch<SetStateAction<Message[]>>;
  inputValue: string;
  setInputValue: Dispatch<SetStateAction<string>>;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setIsLoadingFadingOut: Dispatch<SetStateAction<boolean>>;
  isStreaming: boolean;
  setIsStreaming: Dispatch<SetStateAction<boolean>>;
  setIsThinking: Dispatch<SetStateAction<boolean>>;
  sessionExpired: boolean;
  pageContext: ReturnType<typeof chatAgentService.getPageContext>;
  // retry engine (shell-owned: its effects must keep their mount order)
  retryAttempt: number | null;
  retryAttemptRef: MutableRefObject<number | null>;
  retrySchedulerRef: MutableRefObject<RetryScheduler | null>;
  autoRetryHandlerRef: MutableRefObject<(() => void) | null>;
  setRetryAttempt: Dispatch<SetStateAction<number | null>>;
  /** TRAC-862: pass the failing turn's own re-send thunk so the retry doesn't
   *  have to reconstruct what to send from the rendered transcript. */
  scheduleAutoRetry: (resend?: () => void) => void;
  sessionIdNotifiedRef: MutableRefObject<boolean>;
  // scroll machinery (useScrollBehavior)
  shouldAutoScrollRef: MutableRefObject<boolean>;
  isProgrammaticScrollRef: MutableRefObject<boolean>;
  lockAutoScrollRef: MutableRefObject<boolean>;
  lastTurnWrapperRef: RefObject<HTMLDivElement | null>;
  lastUserMsgRef: RefObject<HTMLDivElement | null>;
  doScrollTo: (options: ScrollToOptions) => void;
  getElementScrollOffset: (el: HTMLElement) => number;
  getViewportHeight: () => number;
  scheduleProgrammaticScrollEnd: (delayMs: number) => void;
  /** TRAC-1132 PR 2: true when the phone scroll rules took this send (useScrollBehavior). */
  beginPhoneSend?: () => boolean;
  // YMMV (useYMMV)
  activateYmmvMode: (prefill?: string, source?: "starter" | "agent" | "user" | "card") => void;
  handleTireSizeSelect: (vehicle: YMMVMatch, tireSize: string, shopUrl?: string) => void;
  ymmvIntentRef: MutableRefObject<string | undefined>;
  ymmvPrefillSizeRef: MutableRefObject<string | undefined>;
  pendingYmmvTimingRef: MutableRefObject<{ elapsedMs: number; openedAt: number } | null>;
  pendingSelectorCompletionRef: MutableRefObject<SelectorCompletion | null>;
  // vehicle context
  pendingVehicleContextRef: MutableRefObject<ActiveVehicle | null>;
  // session expiry (useSessionExpiry)
  triggerExpiryResend: (resendFn: () => void) => void;
  // welcome commit (shell-owned, shared with card selection)
  buildCommittedWelcome: (selectedValue?: string) => Message | null;
  // TRAC-732: live-human handoff (shell-owned state/refs). While a handoff is active the
  // hook routes sends to LP instead of the agent; it catches the `human_handoff` control
  // event mid-stream and consumes the pending handoff at stream-complete.
  handoffStateRef: MutableRefObject<'idle' | 'connecting' | 'queued' | 'active' | 'ended'>;
  setHandoffActivity: Dispatch<SetStateAction<'typing' | 'awaiting' | null>>;
  setRoutingPending: Dispatch<SetStateAction<boolean>>;
  pendingHandoffRef: MutableRefObject<boolean>;
  pendingHandoffOfferRef: MutableRefObject<boolean>;
  pendingHandoffDeptRef: MutableRefObject<string | null>;
  pendingSoftHandoffDeptRef: MutableRefObject<string | null>;
  escalateToHumanRef: MutableRefObject<(initiatedBy?: HandoffInitiator) => void>;
  // TRAC-1212: who started the handoff the agent asked for this turn (from its human_handoff
  // event), read by the shell when that handoff actually starts. Measurement only.
  agentHandoffInitiatorRef?: MutableRefObject<HandoffInitiator | null>;
  // TRAC-475: set when the agent's `human_handoff` asks to connect NOW, until escalateToHuman
  // starts; persistHandoffAttemptRef writes it to the tab's handoff record straight away, so a
  // reload before the stream ends still leaves the next page an ask to send.
  agentEscalateDueRef: MutableRefObject<boolean>;
  persistHandoffAttemptRef: MutableRefObject<() => void>;
  // TRAC-732: bumped every time the customer (re)escalates to a human. The AI "welcome back"
  // comeback captures this at send time; if it changes before the comeback finishes streaming,
  // the customer has re-engaged a rep and the comeback is stale — we drop it mid-stream.
  escalateGenRef: MutableRefObject<number>;
}

export function useChatStream(params: UseChatStreamParams) {
  const {
    messages,
    setMessages,
    inputValue,
    setInputValue,
    isLoading,
    setIsLoading,
    setIsLoadingFadingOut,
    isStreaming,
    setIsStreaming,
    setIsThinking,
    sessionExpired,
    pageContext,
    retryAttempt,
    retryAttemptRef,
    retrySchedulerRef,
    autoRetryHandlerRef,
    setRetryAttempt,
    scheduleAutoRetry,
    sessionIdNotifiedRef,
    shouldAutoScrollRef,
    isProgrammaticScrollRef,
    lockAutoScrollRef,
    lastTurnWrapperRef,
    lastUserMsgRef,
    doScrollTo,
    getElementScrollOffset,
    getViewportHeight,
    scheduleProgrammaticScrollEnd,
    beginPhoneSend,
    activateYmmvMode,
    handleTireSizeSelect,
    ymmvIntentRef,
    ymmvPrefillSizeRef,
    pendingYmmvTimingRef,
    pendingSelectorCompletionRef,
    pendingVehicleContextRef,
    triggerExpiryResend,
    buildCommittedWelcome,
    handoffStateRef,
    setHandoffActivity,
    setRoutingPending,
    pendingHandoffRef,
    pendingHandoffOfferRef,
    pendingHandoffDeptRef,
    pendingSoftHandoffDeptRef,
    escalateToHumanRef,
    agentEscalateDueRef,
    persistHandoffAttemptRef,
    escalateGenRef,
    agentHandoffInitiatorRef,
  } = params;

    /**
     * Fire Adobe Analytics events for tool usage
     *
     * Adobe Analytics Event Mapping (configured in Adobe Tag Manager):
     * - event132: vehicle_priorities_for_tdg - When prompting customer for TDG priorities
     * - event133: tdg_link_provided - When providing a TDG link
     * - event134: fitment_lookup - When completing a fitment lookup
     * - event135: tire_research_kb - When performing tire research or knowledge base lookup
     * - event136: reviews_ratings - When retrieving ratings and review data
     * - event137: compare_tires - When comparing tires
     */
    const trackToolAnalytics = (toolName: string): void => {
      logger.debug("[Analytics Debug] Tool completed:", toolName);

      // Send debug info to parent window for analytics test page
      if (window.parent !== window) {
        window.parent.postMessage(
          {
            source: "TRACI-Analytics-Debug",
            type: "tool_completed",
            toolName: toolName,
            timestamp: new Date().toISOString(),
          },
          "*",
        );
        // TRAC-416: Also send to context debug page (use window.top for nested iframes)
        try { window.top?.postMessage({
          source: "TRACI-Context-Debug",
          type: "traci:agent-chunk",
          toolCall: { name: toolName },
          timestamp: new Date().toISOString(),
        }, "*"); } catch { /* cross-origin */ }
      }

      // Map tool names to Adobe Analytics responseType values
      // The event numbers (event132-137) are mapped in Adobe Tag Manager based on responseType
      const TOOL_RESPONSE_TYPE_MAP: Record<string, string> = {
        // Agent orchestrator tools (actual agents that exist)
        tdg_agent: "tdg_link_provided", // event133
        vehicle_fitment_tool: "fitment_lookup", // event134
        tire_research_agent: "tire_research_kb", // event135
        search_agent: "category_search", // event138 - NEW: Category search URLs
        // show_rating_display: "reviews_ratings",    // event136 - TEMPORARILY DISABLED
        // compare_tires_tool: "compare_tires",       // event137 - TODO: Add when tool exists

        // Legacy tool names (if still used)
        generate_tdg_link: "tdg_link_provided", // event133
        get_vehicle_fitment: "fitment_lookup", // event134
        query_tire_knowledge_base: "tire_research_kb", // event135
        // get_survey_ratings_by_tire: "reviews_ratings",   // event136 - TEMPORARILY DISABLED
      };

      const responseType = TOOL_RESPONSE_TYPE_MAP[toolName];
      if (responseType) {
        logger.debug("[Analytics Debug] Firing analytics:", responseType);

        // Send analytics fire info to parent window for analytics test page
        if (window.parent !== window) {
          window.parent.postMessage(
            {
              source: "TRACI-Analytics-Debug",
              type: "analytics_fired",
              responseType: responseType,
              sessionId: chatAgentService.agentSessionId,
              pageName: chatAgentService.getPageContext()?.page_name,
              timestamp: new Date().toISOString(),
            },
            "*",
          );
        }

        // TRAC-965: the old aiTireAgentResponse push (TRAC-360) was retired. Adobe now hears
        // about capabilities through the in-frame announcement + direct custom link in
        // engagementAnalytics / hostAnalytics, once per capability per turn.
      } else {
        logger.debug("[Analytics Debug] No mapping for tool:", toolName);

        // Send no-mapping info to parent window
        if (window.parent !== window) {
          window.parent.postMessage(
            {
              source: "TRACI-Analytics-Debug",
              type: "no_mapping",
              toolName: toolName,
              timestamp: new Date().toISOString(),
            },
            "*",
          );
        }
      }
    };

    /**
     * Send message to agent
     */
    const handleSendMessage = async (
      messageOverride?: string,
      displayOverride?: string,
      skipUserMessage?: boolean, // Skip adding user message if already added
      metadata?: { selectedValue: string; selectedLabel: string }, // Metadata for card selections
      bypassLoadingGuard?: boolean, // TRAC-218: Skip isLoading/isStreaming check for queued selections
      cannedResponse?: { parts: MessagePart[] }, // TRAC-732: stream a client-side reply (routing prompt) instead of hitting the backend
      comebackGuardGen?: number, // TRAC-732: for the AI "welcome back" comeback — the escalate-generation at send time. If it changes mid-stream (customer re-engaged a rep), the comeback is stale and we drop it.
      systemMarker?: boolean, // TRAC-732: tag this turn as a system state marker ("Requesting live chat") so it renders in the big-black handoff-marker style, not a customer bubble.
    ): Promise<void> => {
      let messageToSend = messageOverride || inputValue;
      // TRACI-472: over the backend's limit the send would only be refused; the input bar
      // already shows why. Leave the text in the box so it can be shortened.
      if (!messageOverride && isMessageTooLong(inputValue)) return;
      // displayOverride allows showing a user-friendly message in the UI
      // but we always save the actual message to conversation history
      let displayContent = displayOverride || messageToSend;

      // TRAC-277: Inject pending vehicle context (from dropdown switch) into message
      // TRACI-472: if the tag would push a message under the limit over it, hold the tag for the
      // next send instead of letting the backend refuse a message the customer kept under 8,000.
      const pendingCtx = pendingVehicleContextRef.current;
      const ctxFor = (v: NonNullable<typeof pendingCtx>) =>
        `\n\n[VEHICLE_CONTEXT: Active vehicle is ${v.description}${v.tireSize ? ` with ${v.tireSize} tires` : ""}]`;
      if (pendingCtx && !isMessageTooLong(messageToSend + ctxFor(pendingCtx))) {
        const v = pendingCtx;
        const ctx = ctxFor(v);
        // Set displayOverride to the original text so the UI hides the context tag
        if (!displayOverride) displayOverride = inputValue || messageOverride || messageToSend;
        messageToSend = messageToSend + ctx;
        displayContent = displayOverride;
        pendingVehicleContextRef.current = null;
      }

      // Card/SSN scrub BEFORE the message becomes a bubble or leaves the page.
      // This blanks payment cards in what the customer sees on their own screen
      // (and in the debug postMessage to the parent window). The network sends are
      // scrubbed again at the wire in RealChatAgentService as the hard guarantee.
      // Mirrors the backend redact_critical (shared/pii.py) — keep in lockstep.
      // CPOC-140: fails closed. If redaction throws, "[message withheld]" is what is shown, sent and
      // stored, and the shopper gets the same note; the chat carries on.
      const typed = messageOverride || inputValue;
      const sentRaw = messageToSend;
      const shown = safeRedact(messageToSend);
      messageToSend = shown.text;
      displayContent = safeRedact(displayContent).text;
      let cardRemoved = shown.failed || (hasCardToken(messageToSend) && !hasCardToken(sentRaw));
      // The piece that COMPLETES a card typed across the shopper's own earlier lines is replaced
      // too; earlier pieces already left (known gap, same as the server).
      if (!shown.failed) {
        const priorLines = messages
          .filter((m) => m.role === "user" && !m.isClientOnly && !m.isSyntheticTurn && !m.isSystemMarker)
          .map((m) => m.content || "");
        if (redactCompletingPiece(priorLines, typed.trim()) === CARD_TOKEN && typed.trim()) {
          messageToSend = CARD_TOKEN;
          displayContent = CARD_TOKEN;
          if (displayOverride) displayOverride = CARD_TOKEN;
          cardRemoved = true;
        }
      }

      if (
        messageToSend.trim() &&
        (bypassLoadingGuard || (!isLoading && !isStreaming)) &&
        retryAttempt === null
      ) {
        // TRAC-203: Lock input immediately to prevent double-submission during validation
        setInputValue("");
        // TRAC-732: a real reply (not one of our canned turns) answers the "which team?"
        // question — unlock the Live Chat button. Typing stops a soft-handoff countdown (they
        // chose to keep chatting with the AI), but TRACI-865: the card is no longer deleted.
        // Once it is not the latest message it shows "You're not connected yet" with a working
        // Connect button, because the agent's words above it still say "connecting you now".
        if (!cannedResponse) {
          setRoutingPending(false);
        }
        // TRAC-732: during a live-human handoff the message routes to LivePerson, not the
        // agent — so DON'T raise the agent "thinking" loader or the streaming flag.
        const inHandoff = handoffStateRef.current !== 'idle';
        if (!inHandoff) {
          setIsLoading(true);
          setIsStreaming(true);
        }
        setIsThinking(false);

        // TRACI-1106: no pre-send "does the session still exist" check any more. It ran in
        // parallel with the POST and, when the server had lost the session, wiped the chat
        // and re-sent. Now the message itself carries this browser's conversation when the
        // server may have lost it (RealChatAgentService.sendMessage), so an expired session
        // answers in context, on screen, with no second send.

        // TRAC-203: Don't clear sessionExpired here — expiry notice persists at top of new conversation

        // Create user message object (always needed for sending to agent)
        const userMessage: Message = {
          id: Date.now().toString(),
          role: "user",
          content: messageToSend.trim(), // Always save actual message (e.g., "User selected: Taycan")
          displayContent: metadata ? null : (displayOverride ? displayContent.trim() : null), // Don't show prefix as text
          timestamp: new Date(),
          parts: metadata ? [{
            type: "user_selection_card",
            selectedValue: metadata.selectedValue,
            selectedLabel: metadata.selectedLabel
          }] : undefined,
          // CPOC-140: a card number was removed from this message; shown once as a plain line under it.
          ...(cardRemoved ? { cardRemovedNote: CARD_REMOVED_NOTE } : {}),
          // TRAC-732: messages sent while a human is live belong in the red band
          inHumanZone: handoffStateRef.current !== 'idle',
          // TRAC-732: a canned-response flow (request / cancel / leave) is a synthetic UI
          // turn, not real chat input — don't let it flip the button to direct-escalate.
          ...(cannedResponse ? { isSyntheticTurn: true } : {}),
          // TRAC-732: render this turn in the big-black handoff-marker style, not a customer bubble.
          ...(systemMarker ? { isSystemMarker: true } : {}),
        };

        // TRAC-416: Send context debug info to top window for context debug page (nested iframes)
        if (window.top !== window) {
          try { window.top?.postMessage({
            source: "TRACI-Context-Debug",
            type: "traci:user-message",
            content: userMessage.content,
            displayContent: userMessage.displayContent,
            parts: userMessage.parts,
            pageContext: chatAgentService.getPageContext(),
            sessionId: chatAgentService.agentSessionId,
            timestamp: new Date().toISOString(),
          }, "*"); } catch { /* cross-origin */ }
        }

        // Add user message to UI (unless it's already been added)
        if (!skipUserMessage) {
          // Block the scroll effect — we handle user-message scroll here
          // so the streaming scroll (instant) can't override our smooth scroll.
          // TRAC-1132 PR 2: a phone instead lets the scroll effect settle the new
          // bubble at the bottom (no anchor to the top, no viewport-tall spacer).
          const phoneSend = beginPhoneSend?.() ?? false;
          if (!phoneSend) {
            shouldAutoScrollRef.current = false;
            lockAutoScrollRef.current = true;
          }

          setMessages((prev) => {
            // TRAC-478: On first user action, commit the currently-displayed welcome
            // message into the real messages array so localStorage persists it for reload.
            const needsCommit = prev.every(m => m.isClientOnly) && !prev.some(m => m.isCommittedWelcome);
            const committedWelcome = needsCommit ? buildCommittedWelcome() : null;
            const newMessages = committedWelcome
              ? [committedWelcome, ...prev, userMessage]
              : [...prev, userMessage];
            chatAgentService.saveMessagesToSession(newMessages);
            return newMessages;
          });

          // Scroll to user message after header transition completes (0.25s CSS).
          // To create scroll distance without a visible flash:
          // 1. Save scrollTop  2. Clear minHeight (layout collapses)
          // 3. Restore scrollTop instantly (browser batches — no visible jump)
          // 4. Set new minHeight + smooth scroll to target
          if (!phoneSend) setTimeout(() => {
            const userMsg = lastUserMsgRef.current;
            const wrapper = lastTurnWrapperRef.current;
            if (userMsg) {
              // Set new minHeight directly — no clear/restore dance.
              // The old minHeight may position us at the same scroll, but that's OK;
              // the new content above (previous exchange moved to flat) creates distance.
              if (wrapper) {
                wrapper.style.minHeight = `${getViewportHeight()}px`;
              }
              isProgrammaticScrollRef.current = true;
              // TRAC-862: leave the same gap the layout uses at rest — see
              // ANCHOR_TOP_GAP_PX. THIS is the anchor a normal turn actually
              // runs (verified by instrumenting the scroll container: exactly
              // one scrollTo per turn, from here). The sibling anchors in
              // useScrollBehavior and ChatInterface serve reload, escalate and
              // re-anchor paths; fixing only those left this one flush against
              // the banner and looked like the fix had no effect.
              const targetScroll = Math.max(0, getElementScrollOffset(userMsg) - ANCHOR_TOP_GAP_PX);
              doScrollTo({ top: targetScroll, behavior: "smooth" });
              scheduleProgrammaticScrollEnd(1000);
            }
          }, 300);
        }

        // TRAC-732: canned-response path — the customer clicked "Live Chat" (no message
        // yet). We reuse the ENTIRE real send pipeline above (big user turn + smooth
        // scroll-to-top) so it looks/feels identical to a real send, then — instead of
        // POSTing to the backend — stream a client-side canned reply (the "which team?"
        // routing prompt with its buttons). Same rendering, same typewriter, one pipeline.
        if (cannedResponse) {
          const respId = (Date.now() + 1).toString();
          const cannedText = cannedResponse.parts.find((p) => p.type === 'text')?.content || '';
          setTimeout(() => {
            setIsLoading(false); // the loader hands off to the streaming reply, just like a real turn
            const cannedMsg: Message = {
              id: respId,
              role: 'assistant',
              content: '',
              timestamp: new Date(),
              isStreaming: true,       // drives the typewriter (useTypingAnimation)
              isClientOnly: true,
              // Only the "which team?" prompt (has routing buttons) locks pre-conversation
              // scroll + survives the escalate cleanup; a plain canned reply shouldn't.
              isRoutingPrompt: cannedResponse.parts.some((p) => p.type === 'routing_actions'),
              parts: cannedResponse.parts,
            } as Message;
            setMessages((prev) => {
              const next = [...prev, cannedMsg];
              // TRAC-732: persist the canned reply IMMEDIATELY (as settled — isStreaming:false),
              // not after the ~2s typewriter. Its parts are complete from creation, so a refresh
              // WHILE the routing buttons are on screen still keeps the whole prompt. Without this,
              // only the "Requesting live chat" user turn survived and the prompt/buttons vanished.
              chatAgentService.saveMessagesToSession(
                next.map((m) => (m.id === respId ? { ...m, isStreaming: false } : m)),
              );
              return next;
            });
            const typeMs = Math.min(4500, 400 + cannedText.length * 22);
            setTimeout(() => {
              setIsStreaming(false);
              setMessages((prev) => prev.map((m) => (m.id === respId ? { ...m, isStreaming: false } : m)));
            }, typeMs);
          }, 500); // ~half-second beat before the reply starts, like real latency
          return;
        }

        // Flag to track if error handling with setTimeout will handle cleanup
        // (prevents race condition where finally block sets state before setTimeout fires)
        let skipFinallyCleanup = false;

        try {
          let fullContent = "";
          let firstChunk = true;
          const messageId = (Date.now() + 1).toString();
          // TRAC-965: the capabilities THIS turn shows (TDG link, tire research, category
          // browse), noted as the cards stream and told to the host page once the turn's text
          // has settled. One instance per send, so a turn that never settles leaves nothing
          // behind for the next one and nothing is shared across hook instances.
          const turnCapabilities = new TurnCapabilities();

          const agentMessage: Message = {
            id: messageId,
            role: "assistant",
            content: "",
            timestamp: new Date(),
            isStreaming: true,
            toolCalls: [],
            parts: [{ type: "text", content: "" }],
          };

          chatAgentService.updateStreamState(messageId, 0, true, 0, true); // Reset for new message (isNewMessage=true)

          // Partner-token embed: no Cognito identity to log. Backend derives
          // user context from the token when needed.
          const userId = null;

          // TRAC-232: Consume pending YMMV timing data (set by typeahead handlers)
          const ymmvTiming = pendingYmmvTimingRef.current;
          pendingYmmvTimingRef.current = null;
          // Slice 2 resume: consume the structured pick (set by handleTireSizeSelect) for this send.
          const selectorCompletion = pendingSelectorCompletionRef.current;
          pendingSelectorCompletionRef.current = null;

          // TRAC-732: during a live-human handoff the customer's message goes to
          // LivePerson (the human), not the agent. No agent stream — the human's
          // replies arrive via the persistent handoff listener.
          if (handoffStateRef.current !== 'idle') {
            await chatAgentService.sendHandoffMessage(userMessage.content);
            setHandoffActivity('awaiting'); // "sent · waiting for {name} to reply"
            setIsLoading(false);
            setIsStreaming(false);
            return;
          }

          // Use new connection-first pattern: establish SSE, send message, stream
          for await (const chunk of chatAgentService.sendMessageAndStream(
            userMessage.content,
            userId,
            ymmvTiming,
            selectorCompletion,
          )) {
            // TRAC-732: kill a stale "welcome back" comeback. The customer left the rep, so the AI
            // started generating a comeback — but before it finished, the customer re-engaged a rep
            // (escalateGenRef bumped). A comeback landing now would drop in AFTER the new
            // connect/leave, out of place ("Welcome back, you and Joshua are old friends" stacked
            // under "you're back with me"). Abort the stream and discard the half-built turn.
            // TRACI-863: the backend answered nothing on purpose (another tab already welcomed
            // the customer back, or a rep still has the chat): end this turn with no reply.
            if (comebackGuardGen !== undefined && escalateGenRef.current !== comebackGuardGen
                || chunk.type === "turn_skipped") {
              chatAgentService.closeActiveConnection();
              setMessages((prev) => prev.filter((m) => m.id !== messageId));
              chatAgentService.updateStreamState(null, 0, false);
              return;
            }
            // Handle agent_thinking event FIRST - show processing animation
            // This must be before firstChunk logic to keep isLoading=true
            if (chunk.type === "agent_thinking") {
              setIsThinking(true);
              continue; // Don't process further, just show the animation
            }

            // Clear thinking state when any real content arrives
            if (chunk.type === "content" || chunk.type === "tool_use_start") {
              setIsThinking(false);
            }

            // Only trigger firstChunk for actual content (not empty response_start)
            // or tool_use_start events - this keeps isLoading=true for agent_thinking
            const hasRealContent =
              (chunk.type === "content" &&
                chunk.content &&
                chunk.content.length > 0) ||
              chunk.type === "tool_use_start";

            if (firstChunk && hasRealContent) {
              agentMessage.timestamp = new Date();
              setIsLoading(false);
              setMessages((prev) => {
                const newMessages = [...prev, agentMessage];
                chatAgentService.saveMessagesToSession(newMessages);
                return newMessages;
              });
              firstChunk = false;

              // Notify analytics test page when session ID is first available
              if (
                !sessionIdNotifiedRef.current &&
                chatAgentService.agentSessionId &&
                window.parent !== window
              ) {
                window.parent.postMessage(
                  {
                    source: "TRACI-Analytics-Debug",
                    type: "session_id_set",
                    sessionId: chatAgentService.agentSessionId,
                    timestamp: new Date().toISOString(),
                  },
                  "*",
                );
                sessionIdNotifiedRef.current = true;
                logger.debug(
                  "[Analytics Debug] Session ID set:",
                  chatAgentService.agentSessionId.substring(0, 12) + "...",
                );
              }

            }

            // Capture trace context for Datadog feedback correlation
            if (chunk.type === "trace_context" && chunk.data?.feedback_id) {
              setMessages((prev) => {
                const updatedMessages = prev.map((msg) =>
                  msg.id === messageId
                    ? { ...msg, feedbackId: chunk.data.feedback_id }
                    : msg,
                );
                chatAgentService.saveMessagesToSession(updatedMessages);
                return updatedMessages;
              });
            }

            // TRAC-147: Capture sessionHash from metrics event for session sync
            if (chunk.type === "metrics" && chunk.sessionHash) {
              updateSessionHash(chunk.sessionHash);
            }

            if (chunk.type === "content" && chunk.content) {
              const chunkContent = chunk.content;
              const chunkSequence = chunk.sequence;
              fullContent += chunkContent;
              const currentContent = fullContent;

              setMessages((prev) => {
                const updatedMessages = prev.map((msg) => {
                  if (msg.id === messageId) {
                    // Create immutable copy of parts array
                    const parts = (
                      msg.parts || [{ type: "text", content: "" }]
                    ).map((p) => ({ ...p }));

                    // Append text to the LAST part if it's text, otherwise create
                    // a new text part at the end. This preserves tool positions
                    // in the stream - text before tools stays before, text after stays after.
                    const lastPart = parts[parts.length - 1];

                    if (lastPart && lastPart.type === "text") {
                      // Last part is text - append to it
                      lastPart.content =
                        (lastPart.content || "") + chunkContent;
                    } else {
                      // Last part is not text (tool, selection_cards, etc.)
                      // Create a new text part at the end
                      parts.push({ type: "text", content: chunkContent });
                    }

                    return {
                      ...msg,
                      parts,
                      isStreaming: true,
                    };
                  }
                  return msg;
                });

                chatAgentService.saveMessagesToSession(updatedMessages);
                chatAgentService.updateStreamState(
                  messageId,
                  currentContent.length,
                  true,
                  chunkSequence,
                );
                return updatedMessages;
              });
            } else if (dispatchToolChunk(chunk, messageId, setMessages, { onToolComplete: trackToolAnalytics })) {
              // TRAC-497: tool lifecycle (start/progress/complete/error) — appends the running
              // tool part, patches the matching tool by id-or-name in parts + toolCalls, persists,
              // and captures the TRAC-203 resume sequence. See chatStreamTools.ts.
              // UI Event Types (from UI hooks system)
            } else if (dispatchCardChunk(chunk, messageId, setMessages)) {
              turnCapabilities.note(chunk.type); // TRAC-965: the card is the engagement signal
              // TRAC-497: uniform card chunks (selection/source/ranking cards, rating display,
              // tdg/tire-browse/size-search links, follow-ups, sales contact, navigation,
              // vehicle/tire info, size confirmation) — appended + persisted in chatStreamCards.ts.
            } else if (chunk.type === "ymmv_interface") {
              if (PARTIAL_YMMV_CARD_ENABLED) {
                // TRAC-896: never auto-open the picker. Render an inline
                // partial YMMV card in the message flow instead — alongside the turn's
                // answer text and any other cards. The emit's resume payload (intent /
                // prefill / prefillSize) rides ON the part; the tap handler re-arms the
                // refs from it before opening the existing full-screen machinery, so
                // multiple cards in history each keep their own intent. Persisted like
                // every other card so it survives a reload.
                setMessages((prev) => {
                  const updatedMessages = prev.map((msg) =>
                    msg.id === messageId
                      ? {
                          ...msg,
                          parts: [
                            ...(msg.parts || []),
                            {
                              type: "ymmv_preview_card",
                              prefill: chunk.prefill || undefined,
                              intent: chunk.intent,
                              prefillSize: chunk.prefillSize,
                            } as MessagePart,
                          ],
                        }
                      : msg,
                  );
                  chatAgentService.saveMessagesToSession(updatedMessages);
                  return updatedMessages;
                });
              } else {
                // TRAC-232: Activate full-screen YMMV vehicle search mode
                // Slice 2 resume: remember the intent the agent tagged this picker with, so the
                // completed pick resumes the SAME capability (fitment vs recommend) — not a guess.
                ymmvIntentRef.current = chunk.intent;
                activateYmmvMode(chunk.prefill || undefined, "agent");
                // Slice 2 fitment: if the customer already named a size, keep it (undefined clears any
                // prior value) — the tire-size step is skipped and this size is what the resume checks.
                ymmvPrefillSizeRef.current = chunk.prefillSize;
              }
            } else if (chunk.type === "human_handoff") {
              // TRAC-732: route_to_human fired. escalation "immediate" → connect now;
              // "offer" (or absent) → the 10s soft-handoff countdown. Deferred to
              // stream-end via pendingHandoffRef so it doesn't collide with the still-
              // streaming turn. Stateful control event — NOT a card in CARD_CHUNKS.
              pendingHandoffRef.current = true;
              pendingHandoffOfferRef.current = chunk.escalation !== "immediate";
              const dept = (chunk.department || "").trim();
              if (dept) pendingHandoffDeptRef.current = dept;
              // TRAC-1212: who started it — the shopper asking in words, or the AI's offer.
              if (agentHandoffInitiatorRef) agentHandoffInitiatorRef.current = initiatorFromHandoffEvent(chunk);
              // TRAC-475: the escalate waits for stream-end, but the reply text survives a reload
              // and these refs do not. A reload in that window (2 of 2 on dev, 16 Oct 2026) left
              // "Connecting you to Sales now." over a chat that had asked nobody. Write the ask
              // down now, as a press that has not been sent yet, so the next page sends it.
              if (chunk.escalation === "immediate") {
                agentEscalateDueRef.current = true;
                persistHandoffAttemptRef.current();
              }
            } else if (chunk.type === "error") {
              // TRAC-402: Session was auto-cleared after a deploy — clear local
              // state and let the user resend. This is the safety-net path; the
              // backend retry usually handles this transparently.
              if (chunk.data?.session_auto_cleared) {
                logger.info('[TRAC-402] Session auto-cleared by backend — resending with the chat so far');
                setIsThinking(false);
                // TRACI-1106: keep the chat; drop only this turn's unfinished reply, then resend
                // the same message (its bubble is already on screen) carrying the conversation.
                setMessages((prev) => prev.filter((m) => m.id !== messageId));
                chatAgentService.updateStreamState(null, 0, false);
                triggerExpiryResend(() => handleSendMessage(messageToSend, displayContent, true));
                return;
              }

              // TRAC-862: one policy decision, stated once. See chatStreamErrors.
              const policy = classifyStreamError(chunk.error, chunk.data);
              const isTimeoutError = policy.isTimeout;
              const errorDetails = policy.userMessage;
              // TRACI-472: a refused message gets no "Try again" button (it would resend the same refused text).
              const requestRejected = (chunk.data as { requestRejected?: unknown } | undefined)?.requestRejected === true;

              // Arm recovery BEFORE the fork.
              //
              // The non-timeout branch below ends in an early `return`, and the
              // old code only called scheduleAutoRetry() at the *bottom* of this
              // branch — so every non-timeout error was a dead end: no retry, no
              // exhaustion, and therefore no emergency overlay. Recovery must not
              // depend on which display path we take, so it is hoisted above the
              // fork where no `return` can skip it. Removing `shouldStartCountdown`
              // (which was always true wherever it was read) is the same fix.
              if (policy.shouldAutoRetry) {
                // Hand over the exact resend for THIS turn. Same call the
                // TRAC-402 session-cleared path uses a few lines above, and the
                // reason the retry can no longer no-op: it never has to guess
                // which message to re-send.
                scheduleAutoRetry(() =>
                  handleSendMessage(messageToSend, displayContent),
                );
              }

              if (isTimeoutError) {
                // For timeout errors, keep the existing text-based display
                setMessages((prev) => {
                  // Check if the message was already added to the array
                  const messageExists = prev.some(
                    (msg) => msg.id === messageId,
                  );

                  if (messageExists) {
                    // Update existing message
                    const updatedMessages = prev.map((msg) =>
                      msg.id === messageId
                        ? {
                            ...msg,
                            parts: [
                              {
                                type: "text",
                                content:
                                  "I'm taking longer than usual. Please wait 15 seconds and try asking again.",
                              },
                            ],
                            isStreaming: false,
                            hasError: true,
                            isTimeoutError: true,
                          }
                        : msg,
                    );
                    chatAgentService.saveMessagesToSession(updatedMessages);
                    return updatedMessages;
                  } else {
                    // Message wasn't added yet - create and add it
                    const timeoutMessage: Message = {
                      id: messageId,
                      role: "assistant",
                      content: "",
                      timestamp: new Date(),
                      isStreaming: false,
                      hasError: true,
                      isTimeoutError: true,
                      parts: [
                        {
                          type: "text",
                          content:
                            "I'm taking longer than usual. Please wait 15 seconds and try asking again.",
                        },
                      ],
                    };
                    const newMessages = [...prev, timeoutMessage];
                    chatAgentService.saveMessagesToSession(newMessages);
                    return newMessages;
                  }
                });
              } else {
                // For non-timeout errors, use the error bubble UI with fade-out
                // transition. The customer-visible copy comes from the policy
                // above — TRAC-419 sanitization now lives in chatStreamErrors so
                // it can be tested without rendering this hook.
                logger.error("Agent error (raw):", chunk.error || "");

                // Trigger fade-out animation for the loading indicator
                setIsLoadingFadingOut(true);

                // After animation completes, add/update the message with error state
                setTimeout(() => {
                  setMessages((prev) => {
                    // Check if the message was already added to the array
                    const messageExists = prev.some(
                      (msg) => msg.id === messageId,
                    );

                    if (messageExists) {
                      // Update existing message
                      const updatedMessages = prev.map((msg) =>
                        msg.id === messageId
                          ? {
                              ...msg,
                              parts: [], // Clear parts, error bubble handles display
                              isStreaming: false,
                              hasError: true,
                              errorDetails: errorDetails,
                              isTimeoutError: false,
                              requestRejected,
                            }
                          : msg,
                      );
                      chatAgentService.saveMessagesToSession(updatedMessages);
                      return updatedMessages;
                    } else {
                      // Message wasn't added yet (error occurred before first real content)
                      // Create and add the error message
                      const errorMessage: Message = {
                        id: messageId,
                        role: "assistant",
                        content: "",
                        timestamp: new Date(),
                        isStreaming: false,
                        hasError: true,
                        errorDetails: errorDetails,
                        isTimeoutError: false,
                        requestRejected,
                        parts: [],
                      };
                      const newMessages = [...prev, errorMessage];
                      chatAgentService.saveMessagesToSession(newMessages);
                      return newMessages;
                    }
                  });
                  setIsLoadingFadingOut(false);
                  setIsLoading(false);
                  setIsStreaming(false);
                  setIsThinking(false);
                }, 300); // Match the CSS animation duration

                // Skip finally block cleanup - setTimeout will handle it
                skipFinallyCleanup = true;
                return;
              }

              // TRAC-550 B8 auto-retry is armed above the fork (TRAC-862) — the
              // scheduler handles backoff + jitter + exhaust from there.
              break;
            } else if (chunk.type === "done") {
              // TRAC-550 B8: a successful response ends the retry chain.
              // Reset both the visible attempt counter and the scheduler.
              //
              // TRAC-862: guard on the REF, not the captured state value. This
              // handler lives in a stream closure built at send time; the resend
              // for a retry is itself a closure captured from the FAILING turn's
              // render, where retryAttempt was still null. Reading the captured
              // value there meant a retry that actually succeeded skipped its own
              // cleanup — the answer rendered with "Trying again now… (attempt 1
              // of 4)" still pinned under it, so a recovery looked like a hang.
              // retryAttemptRef is updated in lockstep with the setter and is
              // always current; that is the entire reason it exists.
              if (retryAttemptRef.current !== null) {
                retryAttemptRef.current = null;
                setRetryAttempt(null);
                retrySchedulerRef.current?.abandon();
              }
              // TRAC-147: Capture sessionHash from response_complete event
              if (chunk.sessionHash) {
                updateSessionHash(chunk.sessionHash);
              }
              const finalContentLength = fullContent.length;
              const doneTraceId = chunk.traceId;
              const doneLangfuseTraceUrl = chunk.langfuseTraceUrl;
              setMessages((prev) => {
                const updatedMessages = prev.map((msg) =>
                  msg.id === messageId
                    ? {
                        ...msg,
                        isStreaming: false,
                        ...(doneTraceId ? { traceId: doneTraceId } : {}),
                        ...(doneLangfuseTraceUrl ? { langfuseTraceUrl: doneLangfuseTraceUrl } : {}),
                      }
                    : msg,
                );
                chatAgentService.saveMessagesToSession(updatedMessages);
                chatAgentService.updateStreamState(
                  messageId,
                  finalContentLength,
                  false,
                );
                return updatedMessages;
              });
              setIsStreaming(false);
              // TRAC-965: the turn's text has settled — tell the host page what the agent did
              // for the customer, one event per capability shown this turn (never per chunk).
              const shown = turnCapabilities.flush(window, chatAgentService.agentSessionId);
              if (shown.length) logger.info("[TRAC-965] agent capabilities", shown.map((m) => m.responseType));
              // TRAC-968: the agent answered — tell the host page (Adobe) once per completed
              // response, whatever the agent did to produce it. Only the agent's own stream
              // reaches this branch: the customer's messages, a live rep's replies and the
              // client-side routing prompt (`cannedResponse` returns before the fetch) never
              // do, so nothing here needs a guard. System-started answers (the "welcome back"
              // comeback, the unavailable-desk note) are real answers and count.
              const responded = postAgentResponse(window, chatAgentService.agentSessionId);
              logger.info('[TRAC-968] agent response', responded ?? '(not embedded — no postMessage sent)');
              // TRAC-732: the agent triggered a handoff this turn (human_handoff
              // with escalation) — now that its text has finished rendering, start it.
              if (pendingHandoffRef.current) {
                pendingHandoffRef.current = false;
                const offer = pendingHandoffOfferRef.current;
                pendingHandoffOfferRef.current = false;
                if (offer) {
                  // Soft-handoff: DEFER the card until the agent's reply finishes typing
                  // (onTypingSettled fires it) so it doesn't get pushed down mid-stream.
                  pendingSoftHandoffDeptRef.current = pendingHandoffDeptRef.current || 'Customer Care';
                } else {
                  // Explicit human request → forward immediately (same flow the button used).
                  // TRAC-1212: an immediate connect is the shopper's own ask, unless the agent said otherwise.
                  const who = agentHandoffInitiatorRef?.current ?? 'shopper_asked_ai';
                  setTimeout(() => escalateToHumanRef.current(who), 400);
                }
              }
              break;
            }
          }
        } catch (error) {
          logger.error("Error sending message:", error);

          let errorDetails =
            "Sorry, I encountered an error processing your message. Please try again.";

          // Provide more specific error messages
          if ((error as Error).message?.includes("Backend error")) {
            errorDetails =
              "Backend service is unavailable. Please try again later.";
          } else if ((error as Error).message?.includes("Failed to fetch")) {
            errorDetails =
              "Connection failed. Please check your internet connection and try again.";
          }

          // Use error bubble UI with fade-out transition (consistent with chunk error handling)
          setIsLoadingFadingOut(true);

          setTimeout(() => {
            const errorMessage: Message = {
              id: (Date.now() + 1).toString(),
              role: "assistant",
              content: "",
              timestamp: new Date(),
              isStreaming: false,
              hasError: true,
              errorDetails: errorDetails,
              isTimeoutError: false,
              parts: [],
            };

            setMessages((prev) => {
              const newMessages = [...prev, errorMessage];
              chatAgentService.saveMessagesToSession(newMessages);
              return newMessages;
            });
            setIsLoadingFadingOut(false);
            setIsLoading(false);
            setIsStreaming(false);
            setIsThinking(false);
          }, 300); // Match the CSS animation duration

          // Skip finally block cleanup - setTimeout will handle it
          skipFinallyCleanup = true;
        } finally {
          // TRAC-475: a turn that never reached `done` (error, abort) never starts the
          // handoff it announced, so it must not leave the ask behind either: a stale
          // due flag would write "connecting" on the next save and the next page would
          // connect a shopper who saw the turn fail.
          if (pendingHandoffRef.current) {
            pendingHandoffRef.current = false;
            pendingHandoffOfferRef.current = false;
            if (agentEscalateDueRef.current) {
              agentEscalateDueRef.current = false;
              persistHandoffAttemptRef.current();
            }
          }
          // Only reset states if error handling with setTimeout didn't claim cleanup
          if (!skipFinallyCleanup) {
            setIsLoading(false);
            setIsStreaming(false);
            setIsThinking(false);
          }
        }
      }
    };

  return { handleSendMessage };
}
